import { motion } from "framer-motion";
import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { tasks } from "../../lib/api";
import { Button } from "../shared/Button";
import { cn } from "../../lib/cn";
import type { DecisionCard } from "../../lib/types";

interface Props {
  taskId: string;
  checkpoint: { blocker: string; options?: string[]; completed: string[] } | null;
  onSent: () => void;
}

const INSTRUCT_LABEL = "Tell the agent what to do instead";

/**
 * The Needs Input decision card — the same card Telegram gets: what the task
 * does, how far it got, why it stopped, the findings, and the options.
 * Typed text goes to the agent as instructions; a button sends it along as
 * its note (Send back: what to fix; Merge anyway: a follow-up task).
 */
export function NeedsInputBox({ taskId, checkpoint, onSent }: Props) {
  const [text, setText] = useState("");
  const [reason, setReason] = useState("");
  const [askReason, setAskReason] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Cheap to refetch: the server words each card once per attempt.
  const { data: card, isLoading, refetch: refetchCard } = useQuery({
    queryKey: ["decision-card", taskId, checkpoint?.blocker ?? ""],
    queryFn: () => tasks.decisionCard(taskId),
    retry: false,
  });

  const run = async (call: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await call();
      setText("");
      setReason("");
      setAskReason(null);
      onSent();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed");
      // "Already handled": show the card for the task's current stop, if any.
      void refetchCard();
    } finally {
      setBusy(false);
    }
  };

  const decide = (c: DecisionCard, action: string, extra?: { reason?: string }) =>
    run(() =>
      tasks.decide(taskId, {
        action,
        attempt: c.attempt,
        note: text.trim() || undefined,
        ...extra,
      }),
    );

  const instruct = () => {
    const note = text.trim();
    if (!note) return;
    if (card) void run(() => tasks.decide(taskId, { action: "instruct", attempt: card.attempt, note }));
    else void run(() => tasks.respond(taskId, note));
  };

  return (
    <motion.div
      initial={{ y: 24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      className="border-t border-border-subtle bg-bg-surface px-4 py-3 space-y-2"
    >
      <div className="flex items-center gap-2">
        <span
          className="h-2 w-2 rounded-full motion-safe:animate-pulse"
          style={{ background: "var(--status-paused)" }}
          aria-hidden="true"
        />
        <span className="text-caption text-status-paused">Needs your decision</span>
      </div>

      {card ? (
        <CardBody card={card} />
      ) : isLoading ? (
        <div className="text-small text-text-tertiary">Preparing the decision card…</div>
      ) : (
        checkpoint?.blocker && (
          <div className="text-body text-text-primary whitespace-pre-wrap">{checkpoint.blocker}</div>
        )
      )}

      {card && card.options.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-caption text-text-tertiary">Your options</div>
          {card.options.map((o) => (
            <div key={o.key} className="flex items-center gap-3">
              <Button
                variant={o.recommended ? "primary" : "secondary"}
                size="sm"
                disabled={busy}
                onClick={() =>
                  o.needs_reason ? setAskReason(o.key) : void decide(card, o.key)
                }
              >
                {o.label}
              </Button>
              <span className="text-small text-text-secondary">
                {o.recommended && <span className="text-text-primary">Recommended. </span>}
                {o.consequence}
              </span>
            </div>
          ))}
        </div>
      )}

      {card && askReason && (
        <div className="flex items-center gap-2">
          <input
            autoFocus
            className="flex-1 px-3 py-1.5 bg-bg-inset rounded-md text-small text-text-primary outline-none border border-transparent focus:border-border-strong"
            placeholder="One-line reason for merging anyway"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button
            variant="primary"
            size="sm"
            disabled={busy || !reason.trim()}
            onClick={() => void decide(card, askReason, { reason: reason.trim() })}
          >
            Merge
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setAskReason(null)}>
            Cancel
          </Button>
        </div>
      )}

      <label className="block space-y-1">
        <span className="text-caption text-text-tertiary">
          {card?.instruct_label ?? INSTRUCT_LABEL}
        </span>
        <textarea
          className={cn(
            "w-full px-3 py-2 bg-bg-inset rounded-md min-h-[64px]",
            "border border-transparent hover:border-border-default focus:border-border-strong",
            "font-mono text-[13px] text-text-primary placeholder:text-text-quaternary",
            "outline-none resize-none transition-colors duration-fast ease-out",
          )}
          placeholder="The agent gets this as instructions and the task stays open. A button above sends it along as its note."
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <div className="flex items-center gap-2">
        {err && <span className="text-small text-status-error mono">{err}</span>}
        <div className="flex-1" />
        <Button variant="primary" size="sm" onClick={instruct} disabled={busy || !text.trim()}>
          {busy ? "Sending…" : "Send to the agent"}
        </Button>
      </div>
    </motion.div>
  );
}

function CardBody({ card }: { card: DecisionCard }) {
  return (
    <div className="space-y-1.5 text-body text-text-primary">
      <div className="font-medium">{card.title}</div>
      {(card.project || card.history) && (
        <div className="text-small text-text-tertiary">
          {[card.project, card.history].filter(Boolean).join(" · ")}
        </div>
      )}
      {card.origin && <div className="text-small text-text-tertiary">{card.origin}</div>}
      {card.purpose && (
        <Line label="What it does">{card.purpose}</Line>
      )}
      {card.need && (
        <Line label="Why it was needed">{card.need}</Line>
      )}
      {card.progress.length > 0 && (
        <Line label="Progress">
          {card.progress.map((p, i) => (
            <span key={p.label}>
              {i > 0 && " · "}
              {p.label}{" "}
              <span className={p.ok ? "text-status-success" : "text-status-error"}>
                {p.ok ? "✓" : "✗"}
              </span>
              {p.detail && ` (${p.detail})`}
            </span>
          ))}
        </Line>
      )}
      <Line label="Why it stopped">{card.why}</Line>
      {card.points.length > 0 && (
        <div>
          <span className="text-text-tertiary">{card.points_heading}:</span>
          <ul className="list-disc list-inside space-y-0.5">
            {card.points.map((p, i) => (
              <li key={i}>
                {p.severity && (
                  <span className="text-text-secondary capitalize">{p.severity}: </span>
                )}
                {p.text}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span className="text-text-tertiary">{label}: </span>
      {children}
    </div>
  );
}
